import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { TourV2ContentSections, TOUR_V2_CONTENT_SECTION_LABELS } from 'constants/tours'
import Heading from 'components/Luxkit/Typography/Heading'
import cn from 'clsx'
import zIndex from 'styles/tools/z-index'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import { centreElementInView } from 'lib/window/scrollUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { getIsTourOfferPurchasable } from 'selectors/TourV2/tourV2OfferPageSelectors'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import PriceRowPriceWithCaption from 'components/Luxkit/PricePoints/PriceRowPriceWithCaption'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import TourV2DepartureMonthsCarousel from '../Departures/TourV2DepartureMonthsCarousel'
import PriceRowLuxPlusPriceWithCaption from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusPriceWithCaption'

const StickyPane = styled(Pane)`
  position: fixed;
  width: 100vw;
  z-index: ${zIndex.stickySubHeader};
  top: 0;
  transition: transform 500ms ease-in-out;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  /* Move offscreen by an extra 10px so the shadow isn't visible when header is hidden */
  transform: translateY(calc(-100% - 10px));

  &.visible {
    transform: translateY(0);
  }
`

const TabHeadingContainer = styled(LayoutContainer)`
  ${mediaQueryUp.desktop} {
    padding-top: ${rem(20)};

    &.hide {
      display: none;
    }
  }
`

const MonthHeadingContainer = styled(LayoutContainer)`
  display: none;
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.desktop} {
    &.show {
      display: block;
    }
  }
`

const HeaderTitle = styled(Heading)`
  flex-grow: 1;
  align-self: stretch;
`

const PricingContainer = styled(CSSBreakpoint)`
  flex-shrink: 0;
`

interface Props {
  activeSection?: TourV2ContentSections
  contentSections: Array<TourV2ContentSections>
  onScrollTo: (section: TourV2ContentSections) => void
  purchasableOption?: App.Tours.TourV2OfferPurchasableOption
  title: string
  visible: boolean
  buttonPaletteValue: ProductPalette
  isOfferPurchasable: boolean;
  monthCarouselInView?: boolean;
  departures: Array<App.Tours.TourV2OfferDeparture>;
}

function TourV2StickyHeader(props: Props) {
  const {
    activeSection,
    contentSections,
    isOfferPurchasable,
    onScrollTo,
    purchasableOption,
    title,
    visible,
    buttonPaletteValue,
    monthCarouselInView,
    departures,
  } = props

  const tabButtonsRef = useRef<Record<string, HTMLButtonElement | null>>({})
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (visible) {
      if (ref.current) {
        document.documentElement.style.scrollPaddingTop = rem(ref.current.offsetHeight + 12)
      }
      return () => {
        document.documentElement.style.scrollPaddingTop = rem(12)
      }
    }
  }, [visible])

  useEffect(() => {
    if (activeSection) {
      centreElementInView(tabButtonsRef.current[activeSection])
    }
  }, [activeSection])

  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(purchasableOption)

  const currentTripId = useAppSelector(getImmersiveTripId)
  const { heights: { tripPlannerImmersive: tripPlannerImmersiveHeaderHeight } } = useContext(HeaderHeightContext)

  const offset = currentTripId ? tripPlannerImmersiveHeaderHeight : 0

  const scrollToDepartures = useCallback(() => {
    onScrollTo(TourV2ContentSections.DEPARTURES)
  }, [onScrollTo])

  const scrollToDepartureDates = useCallback(() => {
    onScrollTo(TourV2ContentSections.DEPARTURE_DATES)
  }, [onScrollTo])

  // we also only show them on desktop, but that's done in CSS
  const showMonthFilters = activeSection === TourV2ContentSections.DEPARTURES && !monthCarouselInView

  return (
    <ProductPaletteProvider palette={buttonPaletteValue}>
      <StickyPane
        ref={ref}
        style={{ top: offset }}
        className={cn({ visible })}
        data-testid="tour-v2-sticky-header"
      >
        <MonthHeadingContainer className={cn({ show: showMonthFilters })}>
          <TourV2DepartureMonthsCarousel
            departures={departures}
            onSelect={scrollToDepartureDates}
          />
        </MonthHeadingContainer>
        <TabHeadingContainer className={cn({ hide: showMonthFilters })}>
          <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="center">
            <CSSBreakpoint min="tablet">
              <HeaderTitle variant="heading5" colour="neutral-one">{title}</HeaderTitle>
            </CSSBreakpoint>
            <Group direction="horizontal" gap={12}>
              <PricingContainer min="tablet">
                {!!purchasableOptionDetails && <>
                  {!purchasableOptionDetails.memberPricePerPerson && <PriceRowPriceWithCaption
                    size="M"
                    caption="From"
                    price={purchasableOptionDetails.pricePerPerson}
                    saleUnit="person"
                  />}
                  {!!purchasableOptionDetails.memberPricePerPerson && <PriceRowLuxPlusPriceWithCaption size="M"
                    caption="From"
                    memberPrice={purchasableOptionDetails.memberPricePerPerson}
                    saleUnit="person"
                  />}
                  <PriceRowCaption>
                    {purchasableOptionDetails.roomTypeName}
                  </PriceRowCaption>
                </>}

                {!isOfferPurchasable && <Heading variant="heading5" align="center">
                  Sold Out
                </Heading>}
              </PricingContainer>

              {isOfferPurchasable && <CSSBreakpoint min="tablet">
                <TextButton kind="primary" onClick={scrollToDepartures}>
                  Check Availability
                </TextButton>
              </CSSBreakpoint>}
            </Group>
          </Group>
          <TabButtonsGroup>
            {contentSections.map(contentSection => <TabButton
              key={contentSection}
              ref={(element) => { tabButtonsRef.current[contentSection] = element }}
              size="medium"
              isActive={contentSection === activeSection}
              onClick={() => onScrollTo(contentSection)}
            >
              {TOUR_V2_CONTENT_SECTION_LABELS[contentSection]}
            </TabButton>)}
          </TabButtonsGroup>
        </TabHeadingContainer>
      </StickyPane>
    </ProductPaletteProvider>
  )
}

type MappedProps = Pick<Props, 'isOfferPurchasable' | 'departures'>

export default withOfferPageState<MappedProps, Props>((offerPageState) => ({
  departures: offerPageState.tourV2Variation!.departures,
  isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
}), TourV2StickyHeader)
